import SiteLayout from "@/components/site/SiteLayout";
import { BRAND } from "@/lib/content";

export default function Privacy() {
  return (
    <SiteLayout
      pageTitle="Privacy Policy"
      pageDescription="How Declan Bishop Property Consultant collects, uses and protects your personal information in line with UK GDPR."
      pagePath="/privacy"
    >
      <section className="bg-brand-cream border-b border-brand-navy/10">
        <div className="container-narrow pt-12 pb-12 md:pt-20 md:pb-16">
          <div className="overline">Legal</div>
          <h1 className="mt-6 font-serif text-4xl md:text-6xl text-brand-navy leading-[0.98] tracking-tight">
            Privacy Policy
          </h1>
          <p className="mt-6 text-sm text-brand-navy/55 uppercase tracking-[0.18em]">
            Last updated: December 2025
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="container-narrow py-16 md:py-24 max-w-3xl mx-auto space-y-12 text-brand-navy/85 leading-relaxed">
          <Block title="1. Who we are">
            <p>
              Declan Bishop Property Consultant (&ldquo;we&rdquo;, &ldquo;us&rdquo;) is an independent property consultancy
              operating throughout Lincolnshire and the surrounding areas of the United Kingdom. We are the
              data controller for the personal information you provide via this website.
            </p>
            <p>
              You can contact us at <a className="ink-link text-brand-navy" href={`mailto:${BRAND.email}`}>{BRAND.email}</a> or
              {" "}
              <a className="ink-link text-brand-navy" href={`tel:${BRAND.phone.replace(/\s+/g, "")}`}>{BRAND.phone}</a>.
            </p>
          </Block>

          <Block title="2. What information we collect">
            <p>When you submit an enquiry through our contact form, we collect:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your name</li>
              <li>Your email address</li>
              <li>Your phone number (optional)</li>
              <li>The property postcode (optional)</li>
              <li>The service you are enquiring about</li>
              <li>The contents of your message</li>
            </ul>
            <p>
              We do not collect payment information through this website, nor do we use cookies for
              advertising or tracking.
            </p>
          </Block>

          <Block title="3. Why we collect it">
            <p>We use the information you provide to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Respond to your enquiry and provide a quote</li>
              <li>Carry out the property services you instruct us to perform</li>
              <li>Keep a record of business correspondence as required by professional and legal obligations</li>
            </ul>
            <p>
              Our legal basis for processing is your consent (when you submit the contact form) and
              legitimate interest in responding to enquiries about our services.
            </p>
          </Block>

          <Block title="4. How long we keep it">
            <p>
              Enquiry data is retained for up to <strong>7 years</strong> in line with HMRC record-keeping
              requirements and professional indemnity obligations. After that period it is securely
              deleted.
            </p>
          </Block>

          <Block title="5. Who we share it with">
            <p>
              We never sell or share your personal data with third parties for marketing purposes. We
              use a small number of trusted processors to deliver our service:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Resend</strong> — to deliver enquiry notification emails (data processor in the EU/US)</li>
              <li><strong>MongoDB Atlas</strong> — secure cloud database hosting your enquiry record</li>
              <li><strong>Emergent</strong> — our website hosting provider</li>
            </ul>
            <p>
              We may also disclose information where required by law (e.g. fraud prevention,
              regulatory investigation, court order).
            </p>
          </Block>

          <Block title="6. Your rights under UK GDPR">
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of any inaccurate data</li>
              <li>Request deletion of your data (where we are no longer legally required to retain it)</li>
              <li>Object to or restrict our processing of your data</li>
              <li>Request a copy of your data in a portable format</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p>
              To exercise any of these rights, email us at{" "}
              <a className="ink-link text-brand-navy" href={`mailto:${BRAND.email}`}>{BRAND.email}</a>.
              You also have the right to complain to the UK Information Commissioner&apos;s Office
              (<a className="ink-link text-brand-navy" href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a>).
            </p>
          </Block>

          <Block title="7. Cookies">
            <p>
              This website does not currently use marketing or analytics cookies. Only strictly
              necessary cookies are used to provide core site functionality. If this changes in the
              future, we will update this policy and add a cookie banner.
            </p>
          </Block>

          <Block title="8. Security">
            <p>
              Information is transmitted over encrypted HTTPS connections and stored in secured
              databases with restricted access. Despite our reasonable precautions, no method of
              transmission over the internet is 100% secure.
            </p>
          </Block>

          <Block title="9. Changes to this policy">
            <p>
              We may update this policy from time to time. The &ldquo;last updated&rdquo; date at the top of this
              page will always reflect the most recent version.
            </p>
          </Block>
        </div>
      </section>
    </SiteLayout>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <h2 className="font-serif text-2xl md:text-3xl text-brand-navy leading-tight mb-4">{title}</h2>
      <div className="space-y-4 text-base">{children}</div>
    </div>
  );
}
